import { useCallback, useEffect, useRef, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

export const useMobileLayout = (): boolean => {
	const theme = useTheme();

	// When using breakpoints.down, it didn't match css's max-width media query
	return useMediaQuery(`(max-width:${theme.breakpoints.values.sm}px)`, {
		matchMedia: (query) => {
			return window.matchMedia(query);
		},
	});
};

export function useResizeListener<ElementType extends HTMLElement>(
	onResize: (elem: ElementType) => void,
): (elem?: ElementType | null) => void {
	const elementRef = useRef<ElementType>();
	const resizeObserverRef = useRef<ResizeObserver>();

	const onElement = useCallback(
		(elem?: ElementType | null) => {
			if ((elem ?? undefined) !== elementRef.current) {
				resizeObserverRef.current?.disconnect();
				if (elem) {
					resizeObserverRef.current = new ResizeObserver(() => onResize(elem));
					resizeObserverRef.current.observe(elem);
					onResize(elem);
				}
				elementRef.current = elem ?? undefined;
			}
		},
		[onResize],
	);

	useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

	return onElement;
}

export const useOverflow = (options?: MutationObserverInit): [(elem?: HTMLElement | null) => void, boolean] => {
	const [overflowing, setOverflowing] = useState(false);
	const elementRef = useRef<HTMLElement>();
	const mutationObserverRef = useRef<MutationObserver>();

	const onChange = useCallback(
		(elem: HTMLElement) => setOverflowing(elem.offsetWidth < elem.scrollWidth || elem.offsetHeight < elem.scrollHeight),
		[],
	);
	const onResize = useResizeListener(onChange);

	const onElement = useCallback(
		(elem?: HTMLElement | null) => {
			if ((elem ?? undefined) !== elementRef.current) {
				mutationObserverRef.current?.disconnect();
				if (elem) {
					mutationObserverRef.current = new MutationObserver(() => onChange(elem));
					mutationObserverRef.current.observe(elem, {
						childList: options?.childList ?? true,
						attributes: options?.attributes ?? true,
						characterData: options?.characterData ?? true,
						subtree: options?.subtree ?? true,
					});
					onChange(elem);
				}
				elementRef.current = elem ?? undefined;
			}
			onResize(elem);
		},
		[onChange, onResize, options?.attributes, options?.characterData, options?.childList, options?.subtree],
	);

	useEffect(() => () => mutationObserverRef.current?.disconnect(), []);

	return [onElement, overflowing];
};

export function useScrollIntoView<T extends HTMLElement>() {
	const ref = useRef<T | null>(null);

	const scrollIntoView = useCallback(() => {
		if (ref.current) {
			ref.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, []);

	return {
		ref,
		scrollIntoView,
	};
}

export function useFocusElementOnMount<T extends HTMLElement>(focusOnMount?: boolean, onFocus?: () => void) {
	const { ref: scrollRef, scrollIntoView } = useScrollIntoView<T>();

	const setRef = useCallback(
		(node: T | null) => {
			scrollRef.current = node;

			if (node && focusOnMount) {
				requestAnimationFrame(() => {
					scrollIntoView();
					onFocus?.();
				});
			}
		},
		[focusOnMount, onFocus, scrollIntoView, scrollRef],
	);

	return { ref: setRef };
}

export function useImagePreview(file?: File) {
	const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
	useEffect(() => {
		if (!file || !(file instanceof Blob) || !file.type.startsWith("image/")) {
			setPreviewUrl(undefined);
			return;
		}

		const url = URL.createObjectURL(file);
		setPreviewUrl(url);

		return () => {
			if (url) {
				URL.revokeObjectURL(url);
			}
			setPreviewUrl(undefined);
		};
	}, [file]);

	return previewUrl;
}
